import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { color } from "./theme";

/**
 * Resizable vertical panels (Anton, 2026-09-17 07:33 UTC): the room list,
 * the thread panel and the room-info panel can be dragged wider/narrower.
 * The space rail is deliberately NOT resizable.
 *
 * Width is persisted per panel in localStorage so it survives reloads and
 * the desktop app restart. Values are clamped to [min, max] both when read
 * and while dragging, so a stale or hand-edited value cannot break layout.
 */

const STORAGE_PREFIX = "alg.panelWidth.";

export function usePanelWidth(key: string, initial: number, min: number, max: number): [number, (w: number) => void] {
  const storageKey = STORAGE_PREFIX + key;
  const clamp = useCallback((w: number) => Math.min(max, Math.max(min, Math.round(w))), [min, max]);
  const [width, setWidthState] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw === null ? NaN : Number(raw);
      return Number.isFinite(parsed) ? clamp(parsed) : initial;
    } catch {
      return initial;
    }
  });

  /* Пределы могут быть динамическими (потолок колонки списка чатов — доля
     ширины окна, Антон 2026-10-05): при смене min/max сохранённая ширина
     переклампливается, иначе после уменьшения окна колонка осталась бы
     шире нового потолка до первого ручного перетаскивания. */
  useEffect(() => {
    setWidthState((w) => {
      const next = clamp(w);
      if (next !== w) {
        try {
          localStorage.setItem(storageKey, String(next));
        } catch {
          // Storage unavailable: clamp applies for this session only.
        }
      }
      return next;
    });
  }, [clamp, storageKey]);
  const setWidth = useCallback(
    (w: number) => {
      const next = clamp(w);
      setWidthState(next);
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        // Storage unavailable (private mode / quota): width still applies for this session.
      }
    },
    [clamp, storageKey],
  );
  return [width, setWidth];
}

/**
 * Drag handle laid over a panel edge. The parent panel must be
 * `position: relative`. `edge` is the edge of the panel the handle sits on;
 * for a left panel (room list) that is its right edge and dragging right
 * widens it; for right panels (thread, room info) it is the left edge and
 * dragging left widens them.
 */
export function PanelResizeHandle({
  edge,
  width,
  onResize,
  onReset,
  testId,
}: {
  edge: "left" | "right";
  width: number;
  onResize: (width: number) => void;
  /** Double-click restores the default width. */
  onReset?: () => void;
  testId?: string;
}) {
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<{ x: number; width: number } | null>(null);

  /* ЗАХВАТ УКАЗАТЕЛЯ, А НЕ СЛУШАТЕЛИ НА `window` (Антон, 2026-10-06 18:52:
     «передвижение (расширение) левой плашки на мак ещё не работает»,
     подтверждено им же в 19:00 — «in safari also doesnt work»).

     ЗАМЕР: механизм перетаскивания в выложенном пакете ПРИСУТСТВУЕТ —
     разделитель рисуется, `col-resize` ставится, `alg.panelWidth.` пишется
     в localStorage. То есть «не выложено» неверный диагноз: код есть и не
     работает.

     ПРИЧИНА. Слушатели висели на `window`, а захвата указателя не было.
     WebKit (Safari и WKWebView — один движок, поэтому Mac-оболочка
     наследует ровно тот же дефект) перестаёт слать pointer-события, как
     только курсор уходит за пределы ручки шириной 11px — а при
     перетаскивании он уходит сразу. Blink в этом месте мягче, потому в
     Chrome не воспроизводилось и дефект дожил до Mac-клиента.

     `setPointerCapture` адресует события ручке, где бы ни был курсор, и
     слушать их надо НА НЕЙ ЖЕ: с захватом `window` их больше не увидит.
     Освобождение — в `pointerup`/`pointercancel`; без него ручка съела бы
     все последующие клики страницы. */
  useEffect(() => {
    if (!dragging) return;
    const handle = handleRef.current;
    if (!handle) return;
    const move = (e: PointerEvent) => {
      const start = startRef.current;
      if (!start) return;
      const dx = e.clientX - start.x;
      onResize(edge === "right" ? start.width + dx : start.width - dx);
    };
    const stop = (e: PointerEvent) => {
      startRef.current = null;
      if (handle.hasPointerCapture(e.pointerId)) {
        handle.releasePointerCapture(e.pointerId);
      }
      setDragging(false);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", stop);
    handle.addEventListener("pointercancel", stop);
    // While dragging, the whole page shows the resize cursor and text is not selected.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", stop);
      handle.removeEventListener("pointercancel", stop);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [dragging, edge, onResize]);

  const style: CSSProperties = {
    position: "absolute",
    top: 0,
    bottom: 0,
    // Зона захвата 11px (порядок 8–10px из постановки, округлено до
    // нечётного, чтобы 3px-полоса встала ровно по центру шва): тянуть за
    // саму 3px-линию мышью — промах через раз.
    [edge]: -4,
    width: 11,
    cursor: "col-resize",
    zIndex: 5,
    background: "transparent",
    transition: "background-color 120ms",
    touchAction: "none",
  };

  /* ВИДИМАЯ ПОЛОСА (Антон 2026-10-05): раньше ручка была прозрачной до
     наведения, и пользователь не понимал, что колонку вообще можно тянуть.
     Теперь 3px-полоса видна всегда: в покое — сильная граница темы, при
     наведении и перетаскивании — акцентная. Центрируется внутри зоны
     захвата по шву панели. */
  const grip: CSSProperties = {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: "50%",
    width: 3,
    transform: "translateX(-50%)",
    background: dragging || hover ? color.accent : color.borderStrong,
    opacity: dragging ? 1 : hover ? 0.9 : 0.55,
    borderRadius: 2,
    transition: "background-color 120ms, opacity 120ms",
    pointerEvents: "none",
  };

  return (
    <div
      ref={handleRef}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={width}
      data-testid={testId ?? "panel-resize-handle"}
      style={style}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        startRef.current = { x: e.clientX, width };
        setDragging(true);
      }}
      onDoubleClick={onReset}
    >
      <div aria-hidden style={grip} />
    </div>
  );
}
